import {dateFromServer} from "@/functions/dates.ts";
import {maxVersion, minVersion} from "@/functions/versions.ts";
import sdk, {
  GetJourneyStepProps,
  GetJourneyStepSchema,
  JourneyStepGroup,
  JourneyStepId,
  JourneyStepName,
  JourneyStepType,
  Version
} from "@/sdk";
import Journey from "./Journey";

export default class JourneyStep<Data = unknown> {
  readonly journey: Journey;
  readonly group: JourneyStepGroup;
  readonly id: JourneyStepId;
  readonly name: JourneyStepName;
  readonly type: JourneyStepType;
  readonly version: Version;
  readonly dependencies: JourneyStepId[];
  started_at: Date | null;
  ended_at: Date | null;
  is_finished: boolean;
  is_available: boolean;

  data: undefined | Data;
  dataLoaded: boolean;
  dataPromise: undefined | Promise<void>;

  constructor(journey: Journey, id: JourneyStepId, schema: GetJourneyStepSchema, props: GetJourneyStepProps | undefined) {
    this.journey = journey;
    this.id = id;
    this.group = schema.group;
    this.name = schema.content.name;
    this.type = schema.content.type;
    this.version = schema.content.version;
    this.dependencies = schema.dependencies;

    this.started_at = props?.data.meta_data.started_at ? dateFromServer(props.data.meta_data.started_at) : null;
    this.ended_at = props?.data.meta_data.ended_at ? dateFromServer(props.data.meta_data.ended_at) : null;
    
    if(props?.data.meta_data){
      if(Object.hasOwn(props?.data.meta_data, 'is_valid')){
        if(this.type === 'report')
          this.is_finished = !!this.ended_at
        else
          this.is_finished = !!props?.data.meta_data.is_valid
      }
      else if(Object.hasOwn(props?.data.meta_data, 'is_finished')){
        this.is_finished = !!props?.data.meta_data.is_finished
      }
      else if(Object.hasOwn(props?.data.meta_data, 'ended_at')){
        this.is_finished = !!this.ended_at
      }
    }
    else{
      this.is_finished = false
    }
    
    this.is_available = props?.data.meta_data.is_available ?? false; // synthesis only

    this.data = undefined;
    this.dataLoaded = false;
    this.dataPromise = undefined;
  }

  async loadData(forceReload: boolean = false): Promise<void> {
    if (this.dataLoaded && !forceReload) return
    if (this.dataPromise) return this.dataPromise;

    this.dataPromise = (async () => {
      try {
        const response = await sdk.getJourneyStep(this.journey.public_id, this.id);
        this.data = response.data.data;
        this.dataLoaded = true;
      } catch (e) {
        this.data = undefined;
        this.dataLoaded = false;
      } finally {
        this.dataPromise = undefined;
      }
    })();
    return this.dataPromise;
  }

  async putData(raw_data: Data extends { raw_data: any } ? Data['raw_data'] : never): Promise<void> {
    const response = await sdk.putJourneyStep(this.journey.public_id, this.id, {
      meta_data: {
        is_valid: true,
        started_at: new Date().toISOString(),
        ended_at: new Date().toISOString(),
        versions: {
          hub_omind: import.meta.env.VITE_HUB_OMIND_VERSION
        }
      },
      raw_data: raw_data
    })
    this.is_finished = true
  }

  get allDependenciesFinished(): boolean {
    return this.dependencies
      .map(stepId => this.journey.getStepById(stepId))
      .every(step => step?.is_finished)
  }

  get canBeStarted(): boolean {
    if (this.is_finished)
      return false

    return this.allDependenciesFinished;
  }

  get started(): boolean {
    return !this.is_finished && !!this.started_at
  }

  minVersion(version: Version): boolean {
    return minVersion(this.version, version)
  }

  maxVersion(version: Version): boolean {
    return maxVersion(this.version, version)
  }

  getDependencyOrder(): number {
    if (!this.dependencies) return 0;
    const childrenOrders = this.dependencies
      .map(id => this.journey.getStepById(id))
      .map(step => (step as JourneyStep).getDependencyOrder());
    return Math.max(...childrenOrders, -1) + 1;
  }
}
